// How far a project got, as a badge.
//
// The console USED to show `projects.status` here, which reads 'draft' on every
// real row (issue #461) — a column that renders the same word forever tells an
// operator nothing. The backend now derives a real rung per project
// (`recruiter/repo/project_stage.py`) off what the project actually produced,
// and this module is the only place that rung becomes a label and a colour.
//
// The colour is doing real work, not decoration: an operator scanning a hundred
// rows should be able to see where the funnel thins out without reading a word.
// So the ladder runs cool → warm → done rather than assigning each rung an
// arbitrary colour.
//
// EIGHT rungs, and the shared tone axis only offers four usable hues here (grey,
// blue, amber, green — `accent` and `success` both render GREEN in this palette,
// and `danger` red would read as an error). So the badge's VARIANT axis carries
// the other half: faint outline → soft fill → solid fill within each hue. That
// keeps every rung distinguishable at a glance, which a hue-only map did not:
// the first version painted `concierge` accent and `finalist` success and the
// two most consequential rungs came out the same green.
//
// The top two rungs share the green hue and split on that variant axis, which is
// exactly what it is for: `finalist` (soft) is the carousel going up, `picked`
// (solid) is the client pressing a card. They used to be ONE rung, labelled "the
// client picked a talent" and fed by a column stamped at reveal — so the badge
// said a client had hired someone the moment we showed them options.
import type { ProjectStage } from "@recruiter/shared/types";

/** The Badge axes this ladder uses. Declared locally rather than imported, same
 *  as `alert-cell.tsx` does for its reason tones. */
type StageTone = "neutral" | "info" | "warning" | "success";
type StageVariant = "outline" | "soft" | "solid";

/** Lowest rung first. Mirrors the backend `PROJECT_STAGES` tuple, and the index
 *  IS the sort order for the Stage column. */
export const PROJECT_STAGES: readonly ProjectStage[] = [
  "new",
  "intake",
  "brief",
  "search",
  "concierge",
  "offers",
  "finalist",
  "picked",
];

export interface StageDisplay {
  label: string;
  tone: StageTone;
  variant: StageVariant;
  /** What produced this rung. Rendered as the badge's tooltip, so an operator
   *  who wonders why a row says "concierge" gets the answer on hover instead of
   *  guessing at a vocabulary nobody wrote down for them. */
  hint: string;
}

/**
 * Exhaustive by construction: `Record<ProjectStage, …>` means a rung added to
 * the backend ladder (and so to the generated union) fails typecheck here rather
 * than shipping as an unlabelled pill.
 */
const DISPLAY: Record<ProjectStage, StageDisplay> = {
  // Grey, and the faintest pill on the ladder: nothing has happened here.
  new: {
    label: "New",
    tone: "neutral",
    variant: "outline",
    hint: "Nothing yet: no client message, no brief, no run.",
  },
  intake: {
    label: "Intake",
    tone: "neutral",
    variant: "soft",
    hint: "The client has started talking to Mira.",
  },
  // Blue: the self-serve lane, brief then search.
  brief: {
    label: "Brief",
    tone: "info",
    variant: "soft",
    hint: "A brief exists for this project.",
  },
  search: {
    label: "Search",
    tone: "info",
    variant: "solid",
    hint: "A self-serve talent search has run.",
  },
  // Amber: the concierge lane, in flight then answered.
  concierge: {
    label: "Concierge",
    tone: "warning",
    variant: "soft",
    hint: "A concierge run is reaching out to talent.",
  },
  offers: {
    label: "Offers",
    tone: "warning",
    variant: "solid",
    hint: "Talent sent offers back to the client.",
  },
  // Green: the client is at the decision, then past it.
  finalist: {
    label: "Finalists",
    tone: "success",
    variant: "soft",
    hint: "Finalists were revealed to the client.",
  },
  // Solid: the funnel completed.
  picked: {
    label: "Talent picked",
    tone: "success",
    variant: "solid",
    hint: "The client engaged a talent, from a finalist card or from search results.",
  },
};

/** Anything not on the ladder — an older console reading a newer backend. Named
 *  rather than dropped, so a rung we do not know about is visible instead of
 *  looking like "new". */
const UNKNOWN: StageDisplay = {
  label: "Unknown",
  tone: "neutral",
  variant: "outline",
  hint: "This console does not recognise the stage the backend reported.",
};

/** The wire union is OPEN — the backend may add a rung before this bundle ships
 *  (frontend/CLAUDE.md's `assertExhaustive` rule) — so read it defensively. */
export function stageDisplay(stage: string | null | undefined): StageDisplay {
  if (!stage) return DISPLAY.new;
  return DISPLAY[stage as ProjectStage] ?? UNKNOWN;
}

/**
 * Sort value for the Stage column: the rung's index on the ladder, so
 * descending puts the projects furthest along on top. An unknown rung sorts
 * ABOVE every known one (it is newer than this bundle, so it is further along
 * by definition) rather than below "new".
 */
export function stageSortValue(stage: string | null | undefined): number {
  if (!stage) return 0;
  const index = PROJECT_STAGES.indexOf(stage as ProjectStage);
  return index === -1 ? PROJECT_STAGES.length : index;
}
